import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  ExtractSkeleton,
  ScreenGreeting,
  TransactionListSkeleton,
} from "../../../Components/Molecules";
import { GridPagination } from "../../../Components/Molecules/GridHistory";
import {
  BAAS_STATEMENT_PAGE_SIZE,
  MAX_STATEMENT_ITEMS,
  useTransactionsData,
} from "../../../Hooks";
import { useFilter } from "../../../Hooks/useFilter";
import { ExtractDayTimeline } from "../Extract/ExtractDayTimeline";
import { ExtractPeriodSummary } from "../Extract/ExtractPeriodSummary";
import { ExtractTimelineToggleAll } from "../Extract/ExtractTimelineToggleAll";
import { paginateList } from "../Extract/extractFilters";
import { isStatementRefund } from "../../../Utils/buildReceiptFromStatementItem";
import { ReceiptsFilterBar } from "./ReceiptsFilterBar";
import {
  applyReceiptsClientFilters,
  filtersFromReceiptsFilterObj,
  flattenTransactionDayGroups,
  receiptsFilterObjFromFilters,
  regroupTransactionsByDay,
} from "./receiptsFilters";

const DEFAULT_ROWS_PER_PAGE = 15;

export const Receipts = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filters, setFilters] = useState(null);
  const [allExpanded, setAllExpanded] = useState(true);

  const {
    state: transactionsData,
    handleGET: fetchTransactionsGET,
    loading,
  } = useTransactionsData();

  const { getFilterObj, handleApplyFilters } = useFilter();
  const receiptFilterObj = getFilterObj("receipt") as Record<string, string>;

  const buildQuery = useCallback(
    () => ({
      page: 1,
      page_size: BAAS_STATEMENT_PAGE_SIZE,
      fetch_all: true,
      max_items: MAX_STATEMENT_ITEMS,
      start_date: receiptFilterObj.start_date || "",
      end_date: receiptFilterObj.end_date || "",
    }),
    [receiptFilterObj.start_date, receiptFilterObj.end_date],
  );

  useEffect(() => {
    setFilters(filtersFromReceiptsFilterObj(receiptFilterObj));
  }, [
    receiptFilterObj.start_date,
    receiptFilterObj.end_date,
    receiptFilterObj.start_amount,
    receiptFilterObj.end_amount,
    receiptFilterObj.trace_id,
    receiptFilterObj.external_id,
  ]);

  useEffect(() => {
    fetchTransactionsGET(buildQuery());
    // handleGET is not memoized; refresh on date filters only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptFilterObj.start_date, receiptFilterObj.end_date]);

  const flatItems = useMemo(() => {
    const all = flattenTransactionDayGroups(transactionsData?.items);
    // Comprovantes = non-refund ledger movements.
    return all.filter((item) => !isStatementRefund(item));
  }, [transactionsData?.items]);

  const filteredFlatItems = useMemo(
    () => applyReceiptsClientFilters(flatItems, filters),
    [flatItems, filters],
  );

  const paginatedItems = useMemo(
    () => paginateList(filteredFlatItems, page, rowsPerPage),
    [filteredFlatItems, page, rowsPerPage],
  );

  const displayGroups = useMemo(
    () => regroupTransactionsByDay(paginatedItems),
    [paginatedItems],
  );

  const timelineDayKey = useMemo(
    () =>
      displayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [displayGroups],
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [timelineDayKey, page]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredFlatItems.length / rowsPerPage) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredFlatItems.length, rowsPerPage, page]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    handleApplyFilters("receipt", receiptsFilterObjFromFilters(nextFilters));
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const hasApiFilters = Boolean(
    receiptFilterObj.start_date
    || receiptFilterObj.end_date
    || receiptFilterObj.start_amount
    || receiptFilterObj.end_amount
    || receiptFilterObj.trace_id
    || receiptFilterObj.external_id,
  );

  const hasItems = flatItems.length > 0;
  const isInitialLoading = (loading.GET && !hasItems) || filters === null;

  return (
    <div className="w-full">
      <ScreenGreeting
        title={intl.get(`words.receipts`)}
        subTitle={intl.get(`phrases.availableReceipts`, {
          total: filteredFlatItems.length,
        })}
      />

      {isInitialLoading ? (
        <ExtractSkeleton />
      ) : (
        <section>
          <div className="mb-4">
            <ReceiptsFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              filteredItems={filteredFlatItems}
            />
            <ExtractPeriodSummary items={filteredFlatItems} className="mt-4" />
          </div>

          <div className="w-full">
            {loading.GET ? (
              <TransactionListSkeleton rows={hasApiFilters ? 8 : 12} />
            ) : (
              <>
                {displayGroups.length > 0 && (
                  <ExtractTimelineToggleAll
                    allExpanded={allExpanded}
                    onToggle={() => setAllExpanded((prev) => !prev)}
                  />
                )}
                <ExtractDayTimeline
                  dayGroups={displayGroups}
                  allExpanded={allExpanded}
                  onAllExpandedChange={setAllExpanded}
                />
                <div className="mt-4">
                  <GridPagination
                    total={filteredFlatItems.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    currentPageCount={paginatedItems.length}
                    onPageChange={setPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    itemLabel={intl.get("common.transactions")}
                  />
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};
